import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { CrmTabs } from "@/components/admin/CrmTabs";
import { api, money } from "@/lib/api";
import { inputCls, Field, Btn, Th, Td, TableWrap, EmptyRow } from "@/components/admin/ui";

const LOAD_STATUSES = ["booked", "in_transit", "delivered", "invoiced", "paid"];

export default function CrmLoads() {
  const [loads, setLoads] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    carrier_id: "", broker: "", pickup: "", pickup_date: "", delivery: "", delivery_date: "",
    miles: "", gross_rate: "", rate_confirmation: "", pod: "",
  });

  const { carrier_id } = useParams();

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    if (carrier_id) params.carrier_id = carrier_id;
    api.get("/crm/loads", { params }).then(({ data }) => setLoads(data)).catch(() => {});
  }, [q, status, carrier_id]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/crm/carriers").then(({ data }) => {
      setCarriers(data);
      if (carrier_id) setForm((f) => ({ ...f, carrier_id }));
    }).catch(() => {});
  }, [carrier_id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selectedCarrier = carriers.find((c) => c.id === form.carrier_id);
  const feePct = selectedCarrier?.fee_pct ?? 9;
  const gross = Number(form.gross_rate) || 0;
  const miles = Number(form.miles) || 0;
  const previewFee = gross ? (gross * feePct) / 100 : 0;
  const previewRpm = gross && miles ? gross / miles : 0;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/crm/loads", {
        ...form,
        miles: miles || null,
        gross_rate: gross,
        pickup_date: form.pickup_date || null,
        delivery_date: form.delivery_date || null,
        rate_confirmation: form.rate_confirmation || null,
        pod: form.pod || null,
      });
      toast.success("Load added");
      setForm({ carrier_id: carrier_id || "", broker: "", pickup: "", pickup_date: "", delivery: "", delivery_date: "", miles: "", gross_rate: "", rate_confirmation: "", pod: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add load");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const { data } = await api.patch(`/crm/loads/${id}`, { status: newStatus });
      setLoads((prev) => prev.map((l) => (l.id === id ? data : l)));
    } catch {
      toast.error("Failed to update status");
    }
  };

  const makeInvoice = async (loadId) => {
    try {
      const { data } = await api.post("/crm/invoices", { load_id: loadId });
      toast.success(`Invoice ${data.invoice_no} created for ${money(data.amount)}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create invoice");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="crm-loads-page">
      <AdminNav />
      <CrmTabs />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <h1 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl">Loads</h1>
          <div className="flex flex-wrap gap-2">
            <input data-testid="loads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search broker, city, load #..." className={`${inputCls} w-56`} />
            <select data-testid="loads-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} w-auto bg-[#0B0B0B]`}>
              <option value="">All statuses</option>
              {LOAD_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
            <Btn data-testid="loads-add-toggle" onClick={() => setShowForm(!showForm)}><Plus size={13} /> Add Load</Btn>
          </div>
        </div>

        {showForm && (
          <form onSubmit={save} className="border border-white/15 bg-[#101010] p-5 sm:p-7 mb-6" data-testid="load-add-form">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Field label="Carrier *" id="l-carrier">
                <select id="l-carrier" data-testid="load-carrier-select" required value={form.carrier_id} onChange={set("carrier_id")} className={`${inputCls} bg-[#101010]`}>
                  <option value="">Select carrier...</option>
                  {carriers.map((c) => <option key={c.id} value={c.id}>{c.carrier_code} — {c.company}</option>)}
                </select>
              </Field>
              <Field label="Broker *" id="l-broker"><input id="l-broker" data-testid="load-broker-input" required value={form.broker} onChange={set("broker")} className={inputCls} /></Field>
              <Field label="Gross Rate $ *" id="l-gross"><input id="l-gross" data-testid="load-gross-input" type="number" step="0.01" min="1" required value={form.gross_rate} onChange={set("gross_rate")} className={inputCls} /></Field>
              <Field label="Pickup (City, ST) *" id="l-pickup"><input id="l-pickup" data-testid="load-pickup-input" required value={form.pickup} onChange={set("pickup")} className={inputCls} /></Field>
              <Field label="Pickup Date" id="l-pdate"><input id="l-pdate" data-testid="load-pickup-date-input" type="date" value={form.pickup_date} onChange={set("pickup_date")} className={inputCls} /></Field>
              <Field label="Miles" id="l-miles"><input id="l-miles" data-testid="load-miles-input" type="number" step="0.1" min="0" value={form.miles} onChange={set("miles")} className={inputCls} /></Field>
              <Field label="Delivery (City, ST) *" id="l-delivery"><input id="l-delivery" data-testid="load-delivery-input" required value={form.delivery} onChange={set("delivery")} className={inputCls} /></Field>
              <Field label="Delivery Date" id="l-ddate"><input id="l-ddate" data-testid="load-delivery-date-input" type="date" value={form.delivery_date} onChange={set("delivery_date")} className={inputCls} /></Field>
              <Field label="Rate Confirmation #" id="l-rc"><input id="l-rc" data-testid="load-rc-input" value={form.rate_confirmation} onChange={set("rate_confirmation")} className={inputCls} /></Field>
              <Field label="POD / Reference" id="l-pod"><input id="l-pod" data-testid="load-pod-input" value={form.pod} onChange={set("pod")} className={inputCls} /></Field>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 mt-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400" data-testid="load-fee-preview">
                Fee {feePct}% = <span className="text-white">{money(previewFee)}</span>
                {miles > 0 && <> · RPM <span className="text-white">${previewRpm.toFixed(2)}/mi</span></>}
              </p>
              <Btn type="submit" data-testid="load-save-button" disabled={saving}>{saving ? "Saving..." : "Save Load"}</Btn>
            </div>
          </form>
        )}

        <TableWrap testid="loads-table">
          <thead>
            <tr className="border-b border-white/10">
              <Th>Load</Th><Th>Carrier</Th><Th>Broker</Th><Th>Route</Th><Th>Miles</Th><Th>Gross</Th><Th>RPM</Th><Th>Fee</Th><Th>Status</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {loads.length === 0 ? (
              <EmptyRow cols={10}>No loads yet</EmptyRow>
            ) : (
              loads.map((l) => (
                <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors duration-200" data-testid={`load-row-${l.id}`}>
                  <Td className="font-mono text-xs text-neutral-400">{l.load_no}</Td>
                  <Td className="text-neutral-300">{l.carrier_name}</Td>
                  <Td>{l.broker}</Td>
                  <Td className="text-neutral-300">
                    {l.pickup} → {l.delivery}
                    <div className="text-xs text-neutral-500">{[l.pickup_date, l.delivery_date].filter(Boolean).join(" → ")}</div>
                  </Td>
                  <Td className="font-mono text-xs">{l.miles ?? "—"}</Td>
                  <Td className="font-mono text-xs">{money(l.gross_rate)}</Td>
                  <Td className="font-mono text-xs">{l.rpm ? `$${l.rpm}` : "—"}</Td>
                  <Td className="font-mono text-xs">{money(l.fee_amount)}</Td>
                  <Td>
                    <select
                      data-testid={`load-status-${l.id}`}
                      value={l.status}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1.5 bg-[#0B0B0B] border border-white/20 cursor-pointer"
                    >
                      {LOAD_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </Td>
                  <Td>
                    {!["invoiced", "paid"].includes(l.status) && (
                      <button data-testid={`load-invoice-${l.id}`} onClick={() => makeInvoice(l.id)} className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400 hover:text-white underline underline-offset-4">
                        Invoice
                      </button>
                    )}
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </TableWrap>
      </main>
    </div>
  );
}
